'use client';

import React, { type ReactNode } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { ParallaxProvider } from 'react-scroll-parallax';
import { ShopifyProvider, CartProvider } from '@shopify/hydrogen-react';
import type { CountryCode, LanguageCode } from '@shopify/hydrogen-react/storefront-api-types';
import chakraTheme from '../theme';
import { publicRuntimeConfig } from '@utils/publicRuntimeConfig';

interface ProvidersProps {
  children: ReactNode;
  countryIsoCode: string;
  languageIsoCode: string;
}

function toAbsoluteUrl(domain: string): string {
  return domain.includes('://') ? domain : `https://${domain}`;
}

function getShopifyConfig(countryIsoCode: string) {
  if (countryIsoCode === 'CA') {
    return {
      storeDomain: toAbsoluteUrl(publicRuntimeConfig.shopify.caStoreDomain ?? ''),
      storefrontToken: publicRuntimeConfig.shopify.caStorefrontToken ?? '',
    };
  }
  return {
    storeDomain: toAbsoluteUrl(publicRuntimeConfig.shopify.storeDomain ?? ''),
    storefrontToken: publicRuntimeConfig.shopify.storefrontToken ?? '',
  };
}

export default function ClientProviders({
  children,
  countryIsoCode,
  languageIsoCode,
}: ProvidersProps) {
  const { storeDomain, storefrontToken } = getShopifyConfig(countryIsoCode);
  return (
    <ShopifyProvider
      key={storeDomain}
      storeDomain={storeDomain}
      storefrontToken={storefrontToken}
      storefrontApiVersion={publicRuntimeConfig.shopify.apiVersion || '2026-01'}
      countryIsoCode={countryIsoCode as CountryCode}
      languageIsoCode={languageIsoCode as LanguageCode}
    >
      <CartProvider>
        <ParallaxProvider>
          <ChakraProvider theme={chakraTheme}>{children}</ChakraProvider>
        </ParallaxProvider>
      </CartProvider>
    </ShopifyProvider>
  );
}
